import React, { memo } from "react";
import { Link } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaTrashAlt, FaGripVertical, FaPen, FaEye } from "react-icons/fa";
import {
  PRIORITY_STYLES,
  STATUS_STYLES,
} from "./DailyActivityLoggerConstants";
import { getScheduleStatus } from "./DailyActivityLoggerUtils";

export function TaskCheckbox({ checked, faded, disabled, onClick, title }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition ${
        checked
          ? faded
            ? "border-blue-200 bg-blue-100 text-blue-400"
            : "border-[#007BFF] bg-[#007BFF] text-white"
          : "border-gray-300 bg-white text-transparent hover:border-[#007BFF]/60"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <svg viewBox="0 0 16 16" className="h-3 w-3 fill-current">
        <path d="M6.4 11.2 3.2 8l1.13-1.13L6.4 8.93l5.27-5.27L12.8 4.8z" />
      </svg>
    </button>
  );
}

function DailyLogTaskRow({
  task,
  isFinalized,
  selectedDate,
  isSelected,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
  hideDue = false,
  hideDrag = false,
  hideCheckbox = false,
  hideActions = false,
}) {
  const dragDisabled = isFinalized || task.completed || hideDrag;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.id, disabled: dragDisabled });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const schedule = getScheduleStatus(task.scheduled_date, selectedDate);
  const progress = Number(task.progress_percent ?? 0);

  const handleCheckboxClick = () => {
    if (isFinalized) return;
    onToggleSelect(task.id);
  };

  return (
    <tr
      ref={setNodeRef}
      style={rowStyle}
      className={isDragging ? "is-dragging" : isSelected ? "is-selected" : undefined}
    >
      {!hideDrag && (
        <td className="dal-drag-col">
          <button
            type="button"
            {...attributes}
            {...listeners}
            disabled={dragDisabled}
            className="dal-row-action-btn"
            aria-label="Reorder task"
          >
            <FaGripVertical size={12} />
          </button>
        </td>
      )}

      {!hideCheckbox && (
        <td className="client-invoices-xero__check-col">
          <TaskCheckbox
            checked={isSelected}
            faded={false}
            disabled={isFinalized}
            onClick={handleCheckboxClick}
            title={
              isSelected
                ? "Selected"
                : task.completed
                ? "Completed — click to select for bulk actions"
                : "Select task"
            }
          />
        </td>
      )}

      <td>
        <button
          type="button"
          onClick={() => onView(task)}
          className={`dal-task-title-btn${task.completed ? " is-completed" : ""}`}
          title="View task details"
        >
          {task.description || "No description"}
        </button>
        {task.application_id ? (
          <div className="mt-1">
            <Link
              to={
                task.application_type === "migration"
                  ? `/applications/view-migration-application-detail/${task.application_id}`
                  : `/applications/view-education-application-detail/${task.application_id}`
              }
              className="client-invoices-xero__invoice-link"
            >
              {task.application_type === "migration" ? "Migration" : "Education"}{" "}
              #{task.application_id}
            </Link>
          </div>
        ) : null}
        <div className="dal-task-progress">
          <div className="dal-task-progress__bar">
            <div
              className="dal-task-progress__fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="dal-task-progress__label">{progress}%</span>
        </div>
      </td>

      <td>
        {task.priority ? (
          <span
            className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ${
              PRIORITY_STYLES[task.priority] || ""
            }`}
          >
            {task.priority}
          </span>
        ) : (
          "—"
        )}
      </td>

      <td>
        <span
          className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ${
            STATUS_STYLES[task.status] ||
            "border border-gray-200 bg-gray-50 text-gray-500"
          }`}
        >
          {task.status || "Pending"}
        </span>
      </td>

      {!hideDue && (
        <td>
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
              schedule.type === "overdue"
                ? "border-rose-200 bg-rose-50 text-rose-600"
                : schedule.type === "today"
                ? "border-gray-200 bg-gray-50 text-gray-600"
                : schedule.type === "tomorrow"
                ? "border-violet-200 bg-violet-50 text-violet-600"
                : "border-blue-200 bg-blue-50 text-blue-600"
            }`}
          >
            {schedule.label}
          </span>
        </td>
      )}

      <td>
        {task.outcome ? (
          <button
            type="button"
            onClick={() => onView(task)}
            className="dal-notes-link"
            title="View full notes"
          >
            {task.outcome}
          </button>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>

      {!hideActions && (
        <td className="text-end">
          <div className="dal-row-actions">
            <button
              type="button"
              onClick={() => onView(task)}
              className="dal-row-action-btn"
              title="View task"
              aria-label="View task"
            >
              <FaEye size={13} />
            </button>
            <button
              type="button"
              disabled={isFinalized}
              onClick={() => onEdit(task)}
              className="dal-row-action-btn dal-row-action-btn--edit"
              title="Edit task"
              aria-label="Edit task"
            >
              <FaPen size={12} />
            </button>
            <button
              type="button"
              disabled={isFinalized}
              onClick={() => onDelete(task.id)}
              className="dal-row-action-btn dal-row-action-btn--delete"
              title="Delete task"
              aria-label="Delete task"
            >
              <FaTrashAlt size={12} />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

export default memo(DailyLogTaskRow);
